export interface ImportProjectOption {
  id: string;
  name: string;
}

export interface ImportMemberOption {
  id: string;
  name: string;
  email: string;
}

export interface ImportTaskPreview {
  date: string | null;
  title: string;
  durationMinutes: number | null;
  projectName: string | null;
  memberName: string | null;
  errors: string[];
}

export interface ImportValidationOptions {
  projects: ImportProjectOption[];
  members: ImportMemberOption[];
  currentUserId: string;
  fallbackDate: string;
  activeWorkspaceId?: string;
}

export interface ImportProjectPlanItem {
  name: string;
  // True when the project already exists in the destination workspace and
  // will be reused; false when the import will create a new one.
  existing: boolean;
}

export interface ImportMemberPlanItem {
  email: string;
  name: string;
  // workspace member id matched by email; null when no member matches
  // (and the task will be imported unassigned unless the user overrides).
  matchedMemberId: string | null;
}

export interface ImportValidationResult {
  rows: ImportTaskPreview[];
  isValid: boolean;
  fileError?: string;
  // Present when the file includes a projects[] block.
  projectPlan?: ImportProjectPlanItem[];
  // Present when the file includes a members[] block.
  memberPlan?: ImportMemberPlanItem[];
  // Row indexes that will be unassigned because their member did not match a
  // workspace user — the UI uses these to highlight rows and offer a manual
  // member override before submitting.
  unassignedRowIndexes?: number[];
  // A portable migration (or an ambiguous legacy portable file) requires Pro;
  // same-workspace backups remain available on Free.
  importKind?: 'BACKUP' | 'MIGRATION' | 'AMBIGUOUS';
  requiresPro?: boolean;
}

const MAX_TASKS = 500;
const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

export const IMPORT_EXAMPLE_JSON = JSON.stringify(
  {
    tasks: [
      {
        date: '2026-08-03',
        title: 'Refactor auth middleware',
        duration: 60,
      },
      {
        title: 'Task without explicit date uses the selected day',
        duration: 30,
      },
    ],
  },
  null,
  2,
);

function isValidDateOnly(dateString: string): boolean {
  if (!DATE_ONLY_REGEX.test(dateString)) return false;
  const [year, month, day] = dateString.split('-').map(Number);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  if (Number.isNaN(year) || year < 1900 || year > 9999) return false;
  return true;
}

export function resolveImportId(input: string): string {
  // Relay currently encodes ids as base64("Type:id"). Keep accepting the
  // older JSON encoding too because users may still have those files.
  try {
    const decoded = atob(input);
    const separator = decoded.indexOf(':');
    if (!decoded.trimStart().startsWith('{') && separator > 0 && decoded.slice(separator + 1)) {
      return decoded.slice(separator + 1);
    }
    const parsed = JSON.parse(decoded);
    if (typeof parsed.id === 'string' && parsed.id) return parsed.id;
  } catch {
    // ignore
  }
  return input;
}

export function getImportAccessPolicy(
  json: unknown,
  activeWorkspaceId?: string,
): {
  importKind: 'BACKUP' | 'MIGRATION' | 'AMBIGUOUS';
  sourceWorkspaceId: string | null;
  requiresPro: boolean;
  error?: string;
} {
  const defaultPolicy = {
    importKind: 'BACKUP' as const,
    sourceWorkspaceId: null,
    requiresPro: false,
  };

  if (typeof json !== 'object' || json === null || Array.isArray(json)) {
    return defaultPolicy;
  }

  const payload = json as Record<string, unknown>;
  if (payload.mode !== undefined && payload.mode !== 'BACKUP' && payload.mode !== 'MIGRATION') {
    return { ...defaultPolicy, error: 'mode must be BACKUP or MIGRATION' };
  }

  let sourceWorkspaceId: string | null = null;
  if (payload.sourceWorkspaceId !== undefined) {
    if (typeof payload.sourceWorkspaceId !== 'string' || !payload.sourceWorkspaceId.trim()) {
      return { ...defaultPolicy, error: 'sourceWorkspaceId must be a non-empty workspace id' };
    }
    sourceWorkspaceId = resolveImportId(payload.sourceWorkspaceId.trim());
  }

  const destinationId = activeWorkspaceId ? resolveImportId(activeWorkspaceId) : null;
  const hasPortableEntityBlocks = Array.isArray(payload.projects) || Array.isArray(payload.members);
  const hasOriginMetadata = payload.mode !== undefined || payload.sourceWorkspaceId !== undefined;
  const hasIntegritySignature =
    typeof payload.integritySignature === 'string' && payload.integritySignature.length > 0;

  // The browser cannot verify the HMAC because the signing key stays on the
  // server. It can still keep the preview aligned with the backend by
  // treating unsigned modern/portable files as Pro-only; the backend remains
  // authoritative for invalid signatures.
  if ((hasOriginMetadata || hasPortableEntityBlocks) && !hasIntegritySignature) {
    return {
      importKind: payload.mode === 'MIGRATION' || !hasPortableEntityBlocks ? 'MIGRATION' : 'AMBIGUOUS',
      sourceWorkspaceId,
      requiresPro: true,
    };
  }

  const isExplicitMigration = payload.mode === 'MIGRATION';
  const isCrossWorkspace =
    sourceWorkspaceId !== null && sourceWorkspaceId !== destinationId;
  const isAmbiguousLegacyPortableFile = sourceWorkspaceId === null && hasPortableEntityBlocks;

  if (isExplicitMigration || isCrossWorkspace) {
    return {
      importKind: 'MIGRATION',
      sourceWorkspaceId,
      requiresPro: true,
    };
  }

  if (isAmbiguousLegacyPortableFile) {
    return {
      importKind: 'AMBIGUOUS',
      sourceWorkspaceId: null,
      requiresPro: true,
    };
  }

  return { ...defaultPolicy, sourceWorkspaceId };
}

function validateProjectDef(
  raw: unknown,
  index: number,
  errors: string[],
): { id: string; name: string } | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    errors.push(`projects[${index}] must be an object`);
    return null;
  }
  const p = raw as Record<string, unknown>;
  if (typeof p.id !== 'string' || !p.id.trim()) {
    errors.push(`projects[${index}].id is required`);
    return null;
  }
  if (typeof p.name !== 'string' || !p.name.trim()) {
    errors.push(`projects[${index}].name is required`);
    return null;
  }
  return { id: p.id.trim(), name: p.name.trim() };
}

function validateMemberDef(
  raw: unknown,
  index: number,
  errors: string[],
): { id: string; name: string; email: string } | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    errors.push(`members[${index}] must be an object`);
    return null;
  }
  const m = raw as Record<string, unknown>;
  if (typeof m.id !== 'string' || !m.id.trim()) {
    errors.push(`members[${index}].id is required`);
    return null;
  }
  if (typeof m.name !== 'string' || !m.name.trim()) {
    errors.push(`members[${index}].name is required`);
    return null;
  }
  if (typeof m.email !== 'string' || !m.email.trim()) {
    errors.push(`members[${index}].email is required`);
    return null;
  }
  return { id: m.id.trim(), name: m.name.trim(), email: m.email.trim().toLowerCase() };
}

export function validateImportFile(
  json: unknown,
  options: ImportValidationOptions,
): ImportValidationResult {
  const { projects, members, currentUserId, fallbackDate } = options;

  const emptyResult: ImportValidationResult = {
    rows: [],
    isValid: false,
  };

  if (typeof json !== 'object' || json === null || Array.isArray(json)) {
    return { ...emptyResult, fileError: 'JSON must be an object' };
  }

  const payload = json as Record<string, unknown>;

  // The shape of the import is driven by the presence of optional blocks —
  // no version field. With projects[] the server can create missing projects;
  // without it, projectId must already exist in the workspace. Same pattern
  // for members[] and email matching.
  const hasProjectsBlock = Array.isArray(payload.projects);
  const hasMembersBlock = Array.isArray(payload.members);

  if (!Array.isArray(payload.tasks)) {
    return { rows: [], isValid: false, fileError: 'tasks must be an array' };
  }

  if (payload.tasks.length === 0) {
    return { rows: [], isValid: false, fileError: 'tasks must contain at least one item' };
  }

  if (payload.tasks.length > MAX_TASKS) {
    return {
      rows: [],
      isValid: false,
      fileError: `tasks must contain at most ${MAX_TASKS} items`,
    };
  }

  // ---- validate optional blocks ----
  const v2Errors: string[] = [];
  const projectDefs: Array<{ id: string; name: string }> = [];
  const memberDefs: Array<{ id: string; name: string; email: string }> = [];

  if (payload.projects != null && !hasProjectsBlock) {
    v2Errors.push('projects must be an array');
  } else if (hasProjectsBlock) {
    const seenNames = new Map<string, number>();
    for (const [index, raw] of (payload.projects as unknown[]).entries()) {
      const def = validateProjectDef(raw, index, v2Errors);
      if (!def) continue;
      const key = def.name.toLowerCase();
      if (seenNames.has(key)) {
        v2Errors.push(`Duplicate project name found in import file: ${def.name}`);
        continue;
      }
      seenNames.set(key, index);
      projectDefs.push(def);
    }
  }

  if (payload.members != null && !hasMembersBlock) {
    v2Errors.push('members must be an array');
  } else if (hasMembersBlock) {
    const seenIds = new Set<string>();
    for (const [index, raw] of (payload.members as unknown[]).entries()) {
      const def = validateMemberDef(raw, index, v2Errors);
      if (!def) continue;
      if (seenIds.has(def.id)) continue;
      seenIds.add(def.id);
      memberDefs.push(def);
    }
  }

  if (v2Errors.length > 0) {
    return { rows: [], isValid: false, fileError: v2Errors.join('; ') };
  }

  // ---- workspace lookup maps ----
  const accessPolicy = getImportAccessPolicy(json, options.activeWorkspaceId);
  if (accessPolicy.error) {
    return { ...emptyResult, fileError: accessPolicy.error };
  }

  const projectById = new Map(projects.map((p) => [resolveImportId(p.id), p]));
  const projectByName = new Map(projects.map((p) => [p.name.toLowerCase(), p]));
  const memberById = new Map(members.map((m) => [resolveImportId(m.id), m]));
  const memberByEmail = new Map(members.map((m) => [m.email.toLowerCase(), m]));
  const currentMember = members.find((m) => resolveImportId(m.id) === resolveImportId(currentUserId));

  // ---- plans derived from the blocks ----
  const projectPlan: ImportProjectPlanItem[] = projectDefs.map((def) => ({
    name: def.name,
    existing: projectByName.has(def.name.toLowerCase()),
  }));

  const memberPlan: ImportMemberPlanItem[] = memberDefs.map((def) => {
    const matched = memberByEmail.get(def.email);
    return {
      email: def.email,
      name: def.name,
      matchedMemberId: matched ? resolveImportId(matched.id) : null,
    };
  });

  const projectByOriginalId = new Map(projectDefs.map((p) => [p.id, p]));
  const memberByOriginalId = new Map(memberDefs.map((m) => [m.id, m]));

  // ---- task rows ----
  const rows: ImportTaskPreview[] = [];
  const unassignedRowIndexes: number[] = [];

  for (const [index, rawTask] of payload.tasks.entries()) {
    const row: ImportTaskPreview = {
      date: null,
      title: '',
      durationMinutes: null,
      projectName: null,
      memberName: null,
      errors: [],
    };

    if (typeof rawTask !== 'object' || rawTask === null || Array.isArray(rawTask)) {
      row.errors.push(`Task ${index + 1} must be an object`);
      rows.push(row);
      continue;
    }

    const task = rawTask as Record<string, unknown>;

    const rawTitle = typeof task.title === 'string' ? task.title.trim() : '';
    if (!rawTitle) {
      row.errors.push('Title is required');
    } else if (rawTitle.length > 140) {
      row.errors.push('Title must be at most 140 characters');
    } else {
      row.title = rawTitle;
    }

    if (task.date != null) {
      if (typeof task.date !== 'string' || !isValidDateOnly(task.date)) {
        row.errors.push('Date must be a valid YYYY-MM-DD string');
      } else {
        row.date = task.date;
      }
    } else if (isValidDateOnly(fallbackDate)) {
      row.date = fallbackDate;
    } else {
      row.errors.push('Date is required when no fallback date is provided');
    }

    if (task.duration != null) {
      if (
        typeof task.duration !== 'number' ||
        !Number.isInteger(task.duration) ||
        task.duration < 0
      ) {
        row.errors.push('Duration must be a non-negative integer (minutes)');
      } else {
        row.durationMinutes = task.duration;
      }
    } else {
      row.durationMinutes = 0;
    }

    // ---- project resolution ----
    const projectIdInput = typeof task.projectId === 'string' ? task.projectId.trim() : null;
    const projectNameInput = typeof task.projectName === 'string' ? task.projectName.trim() : null;

    if (task.projectId != null && (typeof task.projectId !== 'string' || task.projectId.trim() === '')) {
      row.errors.push('projectId must be a non-empty string');
    } else if (task.projectName != null && typeof task.projectName !== 'string') {
      row.errors.push('projectName must be a string');
    } else if (projectIdInput || projectNameInput) {
      let resolvedProjectName: string | null = null;

      if (hasProjectsBlock && projectIdInput) {
        const def = projectByOriginalId.get(projectIdInput);
        if (!def) {
          row.errors.push(`Project reference not found in projects block: ${task.projectId}`);
        } else {
          resolvedProjectName = projectByName.get(def.name.toLowerCase())?.name ?? def.name;
        }
      } else if (hasProjectsBlock && !projectIdInput && projectNameInput) {
        const def = projectDefs.find((p) => p.name.toLowerCase() === projectNameInput.toLowerCase());
        if (!def) {
          row.errors.push(`Project not found: ${task.projectName}`);
        } else {
          resolvedProjectName = projectByName.get(def.name.toLowerCase())?.name ?? def.name;
        }
      } else {
        // No projects[] block: projectId must exist directly in the workspace
        // (or be a Relay global id for an existing project); projectName is
        // matched case-insensitively.
        let resolved: ImportProjectOption | null = null;
        if (projectIdInput) {
          resolved = projectById.get(resolveImportId(projectIdInput)) ?? null;
        }
        if (!resolved && projectNameInput) {
          resolved = projectByName.get(projectNameInput.toLowerCase()) ?? null;
        }
        if (projectIdInput && !resolved) {
          row.errors.push(`Project not found: ${task.projectId}`);
        } else if (projectNameInput && !resolved) {
          row.errors.push(`Project not found: ${task.projectName}`);
        }
        resolvedProjectName = resolved?.name ?? null;
      }

      row.projectName = resolvedProjectName;
    }

    // ---- member resolution ----
    if (task.memberId != null && (typeof task.memberId !== 'string' || task.memberId.trim() === '')) {
      row.errors.push('memberId must be a non-empty string');
    } else {
      const memberIdInput = typeof task.memberId === 'string' ? task.memberId.trim() : null;

      if (memberIdInput) {
        if (hasMembersBlock) {
          const def = memberByOriginalId.get(memberIdInput);
          if (!def) {
            row.errors.push(`Member reference not found in members block: ${task.memberId}`);
          } else {
            const matched = memberByEmail.get(def.email);
            if (matched) {
              row.memberName = matched.name;
            } else {
              row.memberName = 'Unassigned';
              unassignedRowIndexes.push(index);
            }
          }
        } else {
          const member = memberById.get(resolveImportId(memberIdInput));
          if (member) {
            row.memberName = member.name;
          } else {
            row.errors.push(`Member not found: ${task.memberId}`);
          }
        }
      } else {
        row.memberName = currentMember?.name ?? 'You';
      }
    }

    rows.push(row);
  }

  const isValid = rows.length > 0 && rows.every((r) => r.errors.length === 0);
  return {
    rows,
    isValid,
    projectPlan: hasProjectsBlock ? projectPlan : undefined,
    memberPlan: hasMembersBlock ? memberPlan : undefined,
    unassignedRowIndexes: hasMembersBlock ? unassignedRowIndexes : undefined,
    importKind: accessPolicy.importKind,
    requiresPro: accessPolicy.requiresPro,
  };
}
